import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, Download } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking, paid, expired, error
  const [attempts, setAttempts] = useState(0);
  const pollTimer = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const poll = async (n = 0) => {
      if (n >= 8) {
        setStatus('expired');
        return;
      }
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        setAttempts(n + 1);
        if (res.data.payment_status === 'paid') {
          // Save unlock state based on which package was purchased
          const pendingPkg = localStorage.getItem('pending_package_id') || 'pro_pack';
          localStorage.setItem('pro_session_id', sessionId);
          localStorage.setItem('pro_unlocked', 'true');
          if (pendingPkg === 'bundle_pack') {
            localStorage.setItem('bundle_unlocked', 'true');
            localStorage.setItem('bundle_session_id', sessionId);
          }
          localStorage.removeItem('pending_package_id');
          setStatus('paid');
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        pollTimer.current = setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        console.error(e);
        setStatus('error');
      }
    };

    poll();
    return () => pollTimer.current && clearTimeout(pollTimer.current);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-blue-50 py-20 px-4 flex items-center justify-center">
      <Card className="max-w-md w-full border-2 border-orange-300 shadow-2xl">
        <CardContent className="p-10 text-center space-y-6">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-orange-500 mx-auto animate-spin" />
              <h2 className="text-2xl font-black text-gray-900">Verifying Payment...</h2>
              <p className="text-gray-600">Please hold on while we confirm your purchase.</p>
              <p className="text-xs text-gray-400">Attempt {attempts}/8</p>
            </>
          )}
          {status === 'paid' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-black text-gray-900">🎉 Pro Pack Unlocked!</h2>
              <p className="text-gray-700 text-lg">You can now download PDF drill cards for all 38 exercises.</p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => navigate('/exercises')}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-6 text-lg"
                  data-testid="success-go-exercises"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Browse Exercises
                </Button>
                <Link to="/">
                  <Button variant="outline" className="w-full border-2 border-orange-300">Back to Home</Button>
                </Link>
              </div>
            </>
          )}
          {status === 'expired' && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h2 className="text-2xl font-black text-gray-900">Session Expired</h2>
              <p className="text-gray-700">Your checkout session has expired. Please try again.</p>
              <Button onClick={() => navigate('/pricing')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold">
                Try Again
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h2 className="text-2xl font-black text-gray-900">Something Went Wrong</h2>
              <p className="text-gray-700">We couldn't verify your payment. Please contact support if charged.</p>
              <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold">
                Back to Home
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const PaymentCancel = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-blue-50 py-20 px-4 flex items-center justify-center">
      <Card className="max-w-md w-full border-2 border-orange-300 shadow-2xl">
        <CardContent className="p-10 text-center space-y-6">
          <XCircle className="h-16 w-16 text-gray-400 mx-auto" />
          <h2 className="text-3xl font-black text-gray-900">Payment Cancelled</h2>
          <p className="text-gray-600 text-lg">No worries! You can still use all free exercises.</p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-5"
              data-testid="cancel-try-again"
            >
              Try Again
            </Button>
            <Link to="/exercises">
              <Button variant="outline" className="w-full border-2 border-orange-300">Browse Free Exercises</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
